import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { toast } from '@/lib/notifications';
import { useAuditEvents } from './useAuditEvents';
import type { AuditFilters, AuditEventRecord } from '@/lib/firestoreAudit';
import { AUDIT_LIST_LIMIT } from '@/lib/firestoreAudit';
import { AuditDiffDialog } from './AuditDiffDialog';
import { formatDateTime } from '@/lib/format';

type Props = {
  filters: AuditFilters;
};

const PAGE_SIZE = 20;

/** Busca textual client-side sobre os campos visíveis do evento. */
function matchesAuditSearch(e: AuditEventRecord, text: string): boolean {
  const q = text.trim().toLowerCase();
  if (q === '') return true;
  return (
    e.actorEmail.toLowerCase().includes(q) ||
    e.action.toLowerCase().includes(q) ||
    `${e.targetType}/${e.targetId}`.toLowerCase().includes(q) ||
    (e.reason ?? '').toLowerCase().includes(q)
  );
}

/**
 * Tabela do log de auditoria. Segue o padrão canônico de tabela do Cloudscape
 * (cloudscape.design/examples/react/table.html), espelhando `TenantList`:
 * `useCollection` (filtro textual + ordenação + paginação) + `Header` com
 * contador + `TextFilter` + `Pagination`. NUNCA estilizada por CSS — o
 * componente já vem pronto; usar suas props é o jeito correto.
 *
 * Origem: sem issue — commit 05030f0d1, 2026-04-26 17:26 +0200 (aba Audit do
 * tenant + página /audit global, Sprint 2).
 *
 * Contrato: recebe `filters` (memoizados pelo chamador) e assina via
 * `useAuditEvents`. Busca textual, ordenação e paginação (20/página) são
 * client-side sobre o que veio (até `AUDIT_LIST_LIMIT`; aviso "truncado" no
 * header). ERROR vira toast `audit.errorLoad`. O botão de cada linha abre o
 * `AuditDiffDialog`.
 *
 * Usado por (admin): `routes/AuditPage.tsx` e `TenantAuditTab`.
 *
 * Falhas conhecidas:
 *  - 2026-06-09 (885c1c0dd, 4d7102a9b): a tabela estourava a página dentro
 *    das Tabs de `/tenants/:id` — `Container` com paddings + `Table
 *    variant="embedded"`, nunca `disableContentPaddings`.
 *
 * Histórico:
 *  - 2026-06-09 18:06 +0200 — (e6837c41b) padrão `useCollection` do Cloudscape.
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function AuditList({ filters }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const state = useAuditEvents(filters);
  const [selected, setSelected] = useState<AuditEventRecord | null>(null);
  const lang = i18n.language;

  useEffect(() => {
    if (state.kind === 'ERROR') {
      toast.error(t('audit.errorLoad'));
    }
  }, [state.kind, t]);

  const events = useMemo(
    () => (state.kind === 'READY' ? state.result.events : []),
    [state],
  );
  const truncated = state.kind === 'READY' && state.result.truncated;

  const mono = (v: React.ReactNode): React.JSX.Element => (
    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
  );

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'at',
        header: t('audit.columns.at'),
        sortingComparator: (a: AuditEventRecord, b: AuditEventRecord): number =>
          (a.atMillis ?? 0) - (b.atMillis ?? 0),
        cell: (e: AuditEventRecord): React.ReactNode =>
          e.atMillis != null ? formatDateTime(e.atMillis, lang) : '—',
      },
      {
        id: 'actor',
        header: t('audit.columns.actor'),
        cell: (e: AuditEventRecord): React.ReactNode => mono(e.actorEmail),
      },
      {
        id: 'action',
        header: t('audit.columns.action'),
        cell: (e: AuditEventRecord): React.ReactNode => mono(e.action),
      },
      {
        id: 'target',
        header: t('audit.columns.target'),
        cell: (e: AuditEventRecord): React.ReactNode => mono(`${e.targetType}/${e.targetId}`),
      },
      {
        id: 'reason',
        header: t('audit.columns.reason'),
        cell: (e: AuditEventRecord): React.ReactNode => e.reason ?? '—',
      },
      {
        id: 'actions',
        header: '',
        minWidth: 110,
        cell: (e: AuditEventRecord): React.ReactNode => (
          <Button
            onClick={(): void => {
              setSelected(e);
            }}
          >
            {t('audit.viewDiff')}
          </Button>
        ),
      },
    ],
    [t, lang],
  );

  const { items, collectionProps, filterProps, paginationProps, filteredItemsCount } =
    useCollection(events, {
      filtering: { filteringFunction: matchesAuditSearch },
      sorting: {},
      pagination: { pageSize: PAGE_SIZE },
    });

  return (
    <>
      {/* Padrão IDÊNTICO ao TenantFinanceTab/PendingPage (que NÃO estouram no
          mesmo <Tabs>): <Container header={...}> COM paddings + <Table
          variant="embedded">. É o wrapper de content-padding do Container que
          contém a largura (min-width:0) e ativa o scroll horizontal INTERNO da
          tabela. NÃO usar `disableContentPaddings` — isso remove esse wrapper e
          a Table volta a medir pelo conteúdo e estourar a página. */}
      <Container
        header={
          <Header
            counter={`(${String(filteredItemsCount ?? events.length)})`}
            description={
              truncated ? t('audit.truncated', { limit: AUDIT_LIST_LIMIT }) : undefined
            }
          >
            {t('audit.title')}
          </Header>
        }
      >
        <Table<AuditEventRecord>
          {...collectionProps}
          items={items}
          columnDefinitions={columnDefinitions}
          trackBy="id"
          variant="embedded"
          loading={state.kind === 'LOADING'}
          loadingText={t('common.loading')}
          empty={<Box textAlign="center" color="inherit">{t('audit.empty')}</Box>}
          filter={
            <TextFilter
              {...filterProps}
              filteringPlaceholder={t('common.search')}
              filteringAriaLabel={t('common.search')}
            />
          }
          pagination={<Pagination {...paginationProps} />}
        />
      </Container>
      <AuditDiffDialog
        event={selected}
        onClose={(): void => {
          setSelected(null);
        }}
      />
    </>
  );
}
